import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useEffect } from "react";
import {
  Bold, Italic, Strikethrough, Heading2, Heading3,
  List, ListOrdered, Quote, Code, Link as LinkIcon,
  Undo2, Redo2, Minus,
} from "lucide-react";

/**
 * TipTap rich-text editor for admin blog + opening guide content.
 * Emits HTML via onChange(html).
 *
 * Usage:
 *   <RichTextEditor value={form.content} onChange={(html) => setForm({...form, content: html})} />
 */
export default function RichTextEditor({ value, onChange, placeholder = "İçeriği yazın…" }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-teal underline" },
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      onChange(html === "<p></p>" ? "" : html);
    },
    editorProps: {
      attributes: {
        "data-testid": "rich-editor-area",
        class: "prose prose-sm max-w-none min-h-[200px] px-3 py-2 focus:outline-none",
      },
    },
  });

  // Sync external value updates (e.g., when switching records)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const setLink = () => {
    const prev = editor.getAttributes("link").href;
    const url = window.prompt("Bağlantı URL'i (boş = kaldır):", prev || "https://");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const Btn = ({ onClick, active, children, title, testId }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testId}
      className={`p-1.5 rounded text-xs hover:bg-softbg transition ${
        active ? "bg-navy text-white hover:bg-navy/90" : "text-gray-600"
      }`}
    >
      {children}
    </button>
  );

  return (
    <div className="border border-gray-200 rounded-md bg-white" data-testid="rich-editor">
      <div className="border-b border-gray-200 px-2 py-1 flex flex-wrap gap-1 bg-softbg/60 sticky top-0 z-10">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Kalın" testId="rte-bold"><Bold size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="İtalik" testId="rte-italic"><Italic size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive("strike")} title="Üstü çizili" testId="rte-strike"><Strikethrough size={14} /></Btn>
        <span className="w-px bg-gray-200 mx-1"></span>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="Başlık 2" testId="rte-h2"><Heading2 size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} title="Başlık 3" testId="rte-h3"><Heading3 size={14} /></Btn>
        <span className="w-px bg-gray-200 mx-1"></span>
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Liste" testId="rte-ul"><List size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Sıralı Liste" testId="rte-ol"><ListOrdered size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Alıntı" testId="rte-quote"><Quote size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Kod" testId="rte-code"><Code size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Yatay çizgi" testId="rte-hr"><Minus size={14} /></Btn>
        <span className="w-px bg-gray-200 mx-1"></span>
        <Btn onClick={setLink} active={editor.isActive("link")} title="Bağlantı" testId="rte-link"><LinkIcon size={14} /></Btn>
        <span className="flex-1"></span>
        <Btn onClick={() => editor.chain().focus().undo().run()} title="Geri al" testId="rte-undo"><Undo2 size={14} /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} title="İleri al" testId="rte-redo"><Redo2 size={14} /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
